import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/tr'; // Import Turkish locale
import { useAuthStore } from '../store/useAuthStore';
import i18n from '../i18n';
import { formatKw as formatKwUtil, formatKwFromWatts, formatKwh, formatKwhFromWatts } from './CommonFunctions';

export const formatDisplayPhone = (countryCode, phoneNumber) => {
  const cc = String(countryCode || '90').replace(/\D/g, '') || '90';
  let digits = String(phoneNumber || '').replace(/\D/g, '');
  if (digits.startsWith(cc) && digits.length > 10) digits = digits.slice(cc.length);
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  if (digits.length !== 10) return phoneNumber ? `${cc} ${phoneNumber}` : '';
  return `${cc} (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
};
export { formatKwFromWatts, formatKwh, formatKwhFromWatts };
export const formatKw = formatKwUtil;

export const formatUnixToLocalDate = (timestamp) => {
  const parsedTimestamp = Number(timestamp);

  if (!parsedTimestamp || isNaN(parsedTimestamp)) {
    return 'Invalid Date';
  }
  const date =
    parsedTimestamp > 9999999999
      ? new Date(parsedTimestamp)
      : new Date(parsedTimestamp * 1000);

  return dayjs(date).format('DD.MM.YYYY HH:mm:ss');
};

dayjs.extend(relativeTime);

export const unixToRelativeTime = (unixTimestamp) => {
  if (!unixTimestamp) return 'Never';
  
  // Set locale based on current language
  const currentLang = localStorage.getItem('i18nextLng') || 'en';
  if (currentLang === 'tr') {
    dayjs.locale('tr');
  } else {
    dayjs.locale('en');
  }
  
  return dayjs.unix(unixTimestamp).fromNow(); // Converts to "5 minutes ago", "2 days ago", etc.
};

export const countryCode = import.meta.env.VITE_COUNTRY_CODE || '90';

export const currency = import.meta.env.VITE_CURRENCY || 'INR';

export const formatCurrency = (amount) => {
  let locale = currency === 'INR' ? 'en-IN' : 'tr-TR';
  let zeroValue = currency === 'INR' ? '₹0.00' : '₺0.00';
  if (amount) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  } else {
    return zeroValue;
  }
};

export const useIsEditAllowed = () => {
  const { user } = useAuthStore(); // Get user data from Zustand store

  const allowedRoles = new Set(['ADMIN']);

  return allowedRoles.has(user?.role); // Ensure user.role exists before checking
};

export const useIsEditAllowedWithOperator = () => {
  const { user } = useAuthStore(); // Get user data from Zustand store

  const allowedRoles = new Set(['ADMIN']);

  return allowedRoles.has(user?.role); // Ensure user.role exists before checking
};

/**
 * Safe translation helper that provides automatic fallbacks
 * @param {string} key - Translation key (e.g., 'table.name' or 'namespace:table.name')
 * @param {string} fallback - Fallback text if translation is missing
 * @param {object} options - Additional options for i18n.t()
 * @returns {string} - Translated text or fallback
 */
export const safeT = (key, fallback = null, options = {}) => {
  if (!key) return fallback || '';
  
  // Check if namespace is provided in key format (namespace:key)
  let translationKey = key;
  let namespace = null;
  
  if (key.includes(':')) {
    const parts = key.split(':');
    if (parts.length === 2) {
      namespace = parts[0];
      translationKey = parts[1];
    }
  }
  
  // Prepare options with namespace if provided
  const translationOptions = { ...options, defaultValue: null };
  if (namespace) {
    translationOptions.ns = namespace;
  }
  
  // Try to get translation
  const translation = i18n.t(translationKey, translationOptions);
  
  // If translation exists and is not the same as the key, return it
  if (translation && translation !== translationKey) {
    return translation;
  }
  
  // If no fallback provided, generate one from the key
  if (!fallback) {
    // Convert key to readable text (e.g., 'table.name' -> 'Name')
    const keyParts = translationKey.split('.');
    const lastPart = keyParts[keyParts.length - 1];
    
    // Convert camelCase or snake_case to Title Case
    fallback = lastPart
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  }
  
  return fallback;
};

/**
 * React hook version of safeT for use in components
 * @param {string} key - Translation key
 * @param {string} fallback - Fallback text if translation is missing
 * @param {object} options - Additional options for i18n.t()
 * @returns {string} - Translated text or fallback
 */
export const useSafeT = (key, fallback = null, options = {}) => {
  return safeT(key, fallback, options);
};


function getIntervalTimestamp(timestamp,timeInterval = 5) {
  const date = new Date(timestamp * 1000); // Convert to milliseconds
  const minutes = date.getMinutes();
  const interval = Math.floor(minutes / timeInterval) * timeInterval; // Round down to nearest 10-minute interval
  date.setMinutes(interval, 0, 0); // Set minutes to the start of the interval
  return date;
}



// Function to format SoC data into intervals with SoC
export function formatSOCData(data) {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Step 1: Filter to only include the first event of each new SoCValue
  const filteredData = [];
  let lastSoC = null;
  for (const item of data) {
    if (item.SoCValue !== lastSoC) {
      filteredData.push(item);
      lastSoC = item.SoCValue;
    }
  }

  const groupedData = {};

  // Grouping filtered data by 5-minute intervals
  filteredData.forEach(item => {
      const intervalTimestamp = getIntervalTimestamp(item.created_at, 5).getTime();
      
      if (!groupedData[intervalTimestamp]) {
          groupedData[intervalTimestamp] = [];
      }

      groupedData[intervalTimestamp].push(item);
  });

  // Ensure intervals are sorted by time
  const sortedIntervalTimestamps = Object.keys(groupedData).sort((a, b) => Number(a) - Number(b));

  let prevSoC = null;
  const formattedData = sortedIntervalTimestamps.map(intervalTimestamp => {
      const items = groupedData[intervalTimestamp];

      const intervalStart = new Date(parseInt(intervalTimestamp));
      const intervalEnd = new Date(intervalStart);
      intervalEnd.setMinutes(intervalStart.getMinutes() + 5);

      // Calculate the SoC for the interval
      const totalSoC = items.reduce((sum, item) => sum + item.SoCValue, 0);
      let soc = Math.round(totalSoC / items.length);

      // Ensure SoC never decreases
      if (prevSoC !== null && soc < prevSoC) {
        soc = prevSoC;
      }
      prevSoC = soc;

      // Convert to local time without seconds and time zone abbreviation
      const localIntervalStart = intervalStart.toLocaleString('en-US', { 
        hour: '2-digit', minute: '2-digit', hour12: false
      });
      const localIntervalEnd = intervalEnd.toLocaleString('en-US', { 
        hour: '2-digit', minute: '2-digit', hour12: false
      });

      return {
          intervalStart: localIntervalStart, // Local time formatted
          intervalEnd: localIntervalEnd,     // Local time formatted
          soc,                               // Rounded to nearest integer
          recordsCount: items.length
      };
  });

  return formattedData;
}


export function formatVoltageData(data) {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const grouped = {};

  data.forEach((item) => {
    const intervalTimestamp = getIntervalTimestamp(item.created_at).getTime();

    if (!grouped[intervalTimestamp]) {
      grouped[intervalTimestamp] = [];
    }

    grouped[intervalTimestamp].push(item);
  });

  const result = [];

  Object.entries(grouped).forEach(([intervalTimestamp, entries]) => {
    const timeLabel = new Date(Number(intervalTimestamp)).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const avgChargerVoltage = entries.reduce(
      (sum, item) => sum + (item.chargerSupplyVoltage || 0),
      0
    ) / entries.length;

    const avgEVDemandVoltage = entries.reduce(
      (sum, item) => sum + (item.EVdemandVoltage || 0),
      0
    ) / entries.length;

    result.push(
      {
        time: timeLabel,
        value: parseFloat(avgChargerVoltage.toFixed(2)),
        type: i18n.t('transcationModal:chartLegendChargerVoltageV'),
      },
      {
        time: timeLabel,
        value: parseFloat(avgEVDemandVoltage.toFixed(2)),
        type: i18n.t('transcationModal:chartLegendEvVoltageV'),
      }
    );
  });

  return result;
}

export function formatCurrentData(data) {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const groupedData = {};

  // Grouping data by 5-minute intervals
  data.forEach(item => {
    const intervalTimestamp = getIntervalTimestamp(item.created_at, 5).getTime();
    
    if (!groupedData[intervalTimestamp]) {
      groupedData[intervalTimestamp] = [];
    }

    groupedData[intervalTimestamp].push(item);
  });

  // Format each interval into start and end times, with average currents
  const formattedData = Object.keys(groupedData).map(intervalTimestamp => {
    const items = groupedData[intervalTimestamp];

    const intervalStart = new Date(parseInt(intervalTimestamp));
    const intervalEnd = new Date(intervalStart);
    intervalEnd.setMinutes(intervalStart.getMinutes() + 5);

    // Calculate the average currents for the interval
    const totalChargerCurrent = items.reduce((sum, item) => sum + item.chargerSupplyCurrent, 0);
    const totalEVCurrent = items.reduce((sum, item) => sum + item.EVdemandCurrent, 0);
    const avgChargerCurrent = totalChargerCurrent / items.length;
    const avgEVCurrent = totalEVCurrent / items.length;

    // Convert to local time without seconds and time zone abbreviation
    const localIntervalStart = intervalStart.toLocaleString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false
    });

    return [
      {
        time: localIntervalStart,
        value: parseFloat(avgChargerCurrent.toFixed(2)),
        type: i18n.t('transcationModal:chartLegendChargerCurrentA'),
        isEfficient: Math.abs(avgChargerCurrent - avgEVCurrent) <= 1
      },
      {
        time: localIntervalStart,
        value: parseFloat(avgEVCurrent.toFixed(2)),
        type: i18n.t('transcationModal:chartLegendEvCurrentA'),
        isEfficient: Math.abs(avgChargerCurrent - avgEVCurrent) <= 1
      }
    ];
  }).flat();

  return formattedData;
}

export function formatCurrentDataBySoC(data) {
  if (!Array.isArray(data)) return [];

  const seenSoC = new Set();
  const formattedData = [];

  for (const item of data) {
    if (!seenSoC.has(item.SoCValue)) {
      seenSoC.add(item.SoCValue);

      formattedData.push({
        SoCValue: item.SoCValue,
        chargerSupplyCurrent: parseFloat(item.chargerSupplyCurrent.toFixed(2)),
        EVdemandCurrent: parseFloat(item.EVdemandCurrent.toFixed(2)),
        isEfficient: Math.abs(item.chargerSupplyCurrent - item.EVdemandCurrent) <= 1,
        created_at: Number(item.created_at) * 1000, // (optional) keep time if needed
      });
    }
  }

  // Sort ascending by SoCValue for nice graphs
  formattedData.sort((a, b) => a.SoCValue - b.SoCValue);

  // Remove the first event
  return formattedData.slice(1);
}

// getDeviceToken function removed - Firebase has been removed from the project


  export const parseLogs = (data) => {
    if (!data?.data) return [];
  
    return data.data
      .map((log, index) => {
        const match = log.match(/^\[(.*?)\] (.*)$/);
        if (!match)
          return { key: index, timestamp: 'Unknown', messageDetails: log };
  
        const timestamp = match[1] || 'Unknown';
        const messageDetails = match[2];
  
        return {
          key: index,
          timestamp,
          messageDetails,
        };
      })
      .reverse(); // Show latest messages first
  };

/**
 * Safely extracts body content from HTML and sanitizes it
 * @param {string} html - The HTML content
 * @returns {string} - Sanitized body content
 */
export const extractBodyContent = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  try {
    // Create a temporary DOM element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Extract body content
    const body = doc.querySelector('body');
    if (body) {
      return body.innerHTML;
    }
    
    // If no body tag, check if the content is already body content
    // This handles cases where the HTML might just be body content without html/body tags
    const hasHtmlTag = html.toLowerCase().includes('<html');
    const hasBodyTag = html.toLowerCase().includes('<body');
    
    if (!hasHtmlTag && !hasBodyTag) {
      // Content appears to be body content already, sanitize it
      return sanitizeHtml(html);
    }
    
    // If there's an html tag but no body tag, return the content as is
    if (hasHtmlTag && !hasBodyTag) {
      return sanitizeHtml(html);
    }
    
    return '';
  } catch (error) {
    console.error('Error parsing HTML:', error);
    return '';
  }
};

/**
 * Basic HTML sanitization to prevent XSS attacks
 * @param {string} html - The HTML content to sanitize
 * @returns {string} - Sanitized HTML content
 */
export const sanitizeHtml = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Remove potentially dangerous tags and attributes
  const dangerousTags = /<(script|iframe|object|embed|form|input|textarea|select|button|link|meta|style|title|head|html|body)[^>]*>.*?<\/\1>/gis;
  const dangerousAttributes = /(on\w+\s*=|javascript:|vbscript:|data:text\/html)/gi;
  const dangerousProtocols = /(javascript:|vbscript:|data:text\/html)/gi;
  
  let sanitized = html
    .replace(dangerousTags, '') // Remove dangerous tags
    .replace(dangerousAttributes, '') // Remove dangerous attributes
    .replace(dangerousProtocols, ''); // Remove dangerous protocols
  
  // Allow safe HTML tags and attributes
  const allowedTags = {
    'p': ['class', 'style'],
    'div': ['class', 'style'],
    'span': ['class', 'style'],
    'h1': ['class', 'style'],
    'h2': ['class', 'style'],
    'h3': ['class', 'style'],
    'h4': ['class', 'style'],
    'h5': ['class', 'style'],
    'h6': ['class', 'style'],
    'ul': ['class', 'style'],
    'ol': ['class', 'style'],
    'li': ['class', 'style'],
    'strong': ['class', 'style'],
    'b': ['class', 'style'],
    'em': ['class', 'style'],
    'i': ['class', 'style'],
    'u': ['class', 'style'],
    'a': ['href', 'class', 'style', 'target'],
    'br': [],
    'hr': ['class', 'style'],
    'table': ['class', 'style'],
    'thead': ['class', 'style'],
    'tbody': ['class', 'style'],
    'tr': ['class', 'style'],
    'td': ['class', 'style', 'colspan', 'rowspan'],
    'th': ['class', 'style', 'colspan', 'rowspan'],
    'blockquote': ['class', 'style'],
    'code': ['class', 'style'],
    'pre': ['class', 'style'],
    'img': ['src', 'alt', 'class', 'style', 'width', 'height'],
  };

  // Basic tag filtering (this is a simplified version)
  // In production, you might want to use a more robust library like DOMPurify
  const tagRegex = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/g;
  sanitized = sanitized.replace(tagRegex, (match, slash, tagName, attributes) => {
    const lowerTagName = tagName.toLowerCase();
    
    if (!allowedTags[lowerTagName]) {
      return ''; // Remove disallowed tags
    }
    
    // Filter attributes
    const allowedAttrs = allowedTags[lowerTagName];
    const attrRegex = /(\w+)\s*=\s*["']([^"']*)["']/g;
    let filteredAttrs = '';
    let attrMatch;
    
    while ((attrMatch = attrRegex.exec(attributes)) !== null) {
      const [, attrName, attrValue] = attrMatch;
      if (allowedAttrs.includes(attrName.toLowerCase())) {
        // Additional check for href attributes
        if (attrName.toLowerCase() === 'href') {
          if (attrValue.startsWith('http://') || attrValue.startsWith('https://') || attrValue.startsWith('mailto:') || attrValue.startsWith('tel:')) {
            filteredAttrs += ` ${attrName}="${attrValue}"`;
          }
        } else {
          filteredAttrs += ` ${attrName}="${attrValue}"`;
        }
      }
    }
    
    return `<${slash}${tagName}${filteredAttrs}>`;
  });

  return sanitized;
};